/**
 * MeetingJoinButton — Smart join button for all platforms
 *
 * Usage:
 *   <MeetingJoinButton
 *     platform="zoom"
 *     url="https://zoom.us/j/123456789"
 *     sessionTime="2026-03-15T14:00:00"
 *     size="md"
 *   />
 *
 * Features:
 *   - Platform-specific colours and icons
 *   - Opens Zoom app if installed, falls back to web
 *   - Countdown timer: activates 15 min before session
 *   - Copy link button
 *   - Disabled state with reason when too early
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getPlatform, getMeetingJoinUrl } from '../config/meetingConfig';
import './MeetingJoinButton.css';

// ── Platform SVG icons — clean brand icons, no emoji ──
/**
 * PlatformIcon
 *
 * Props:
 *   id      — 'zoom' | 'teams' | 'google_meet' | 'custom' | any
 *   size    — pixel size (default 18)
 *   color   — icon path / stroke color (default: platform brand color)
 *   bg      — if provided, renders a rounded-square background in this color
 *             with white paths inside. Use this for platform selectors / badges.
 *             If omitted, renders just the paths in `color` (use for buttons).
 *
 * Examples:
 *   <PlatformIcon id="zoom" size={22} bg="#2D8CFF" />     ← blue square + white camera (selector)
 *   <PlatformIcon id="zoom" size={18} color="#fff" />     ← white camera, no background (colored btn)
 *   <PlatformIcon id="zoom" size={14} color="#2D8CFF" />  ← blue camera, no background (badge)
 */
export function PlatformIcon({ id, size = 18, color, bg }) {
  const s = { width: size, height: size, display: 'inline-block', flexShrink: 0, verticalAlign: 'middle' };

  // When bg is supplied → colored rounded-square "app icon" style
  // When bg is not supplied → transparent, paths use `color` prop

  if (id === 'zoom') {
    const pathColor = bg ? '#fff' : (color || '#2D8CFF');
    return (
      <svg style={s} viewBox="0 0 24 24" fill="none">
        {bg && <rect width="24" height="24" rx="5" fill={bg}/>}
        <rect x="3" y="8" width="11" height="8" rx="1.5" fill={pathColor}/>
        <path d="M14 11.2 20 8.5v7l-6-2.7v-1.6z" fill={pathColor}/>
      </svg>
    );
  }

  if (id === 'teams') {
    const pathColor = bg ? '#fff' : (color || '#5059C9');
    return (
      <svg style={s} viewBox="0 0 24 24" fill="none">
        {bg && <rect width="24" height="24" rx="5" fill={bg}/>}
        <circle cx="15" cy="7.5" r="2.4" fill={pathColor} opacity={bg ? '1' : '0.9'}/>
        <rect x="11" y="11" width="8" height="5.5" rx="1.2" fill={pathColor} opacity={bg ? '0.85' : '0.85'}/>
        <circle cx="9" cy="9" r="3" fill={pathColor}/>
        <rect x="4" y="13.5" width="10" height="5.5" rx="1.5" fill={pathColor}/>
      </svg>
    );
  }

  if (id === 'google_meet') {
    const pathColor = bg ? '#fff' : (color || '#1a73e8');
    return (
      <svg style={s} viewBox="0 0 24 24" fill="none">
        {bg && <rect width="24" height="24" rx="5" fill={bg}/>}
        <rect x="2" y="7" width="14" height="11" rx="2" fill={pathColor}/>
        <path d="M16 10.8 22 7.5v9l-6-3.3v-2.4z" fill={pathColor}/>
      </svg>
    );
  }

  // custom / fallback — link chain icon
  const strokeColor = bg ? '#fff' : (color || '#374151');
  return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      {bg && <rect width="24" height="24" rx="5" fill={bg}/>}
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke={strokeColor}/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke={strokeColor}/>
    </svg>
  );
}

// ── How many minutes before can user join ──────────────────────
const EARLY_JOIN_MINUTES = 15;

// ── Parse ISO / "YYYY-MM-DD HH:MM" into Date ──────────────────
function parseSessionTime(dateStr, timeStr) {
  if (!dateStr) return null;
  try {
    if (timeStr) {
      // Convert "3:00 PM" to 24h
      const [time, meridiem] = timeStr.split(' ');
      let [h, m] = time.split(':').map(Number);
      if (meridiem === 'PM' && h !== 12) h += 12;
      if (meridiem === 'AM' && h === 12) h = 0;
      const d = new Date(dateStr);
      d.setHours(h, m, 0, 0);
      return d;
    }
    return new Date(dateStr);
  } catch (_) {
    return null;
  }
}

// ── Countdown hook ─────────────────────────────────────────────
function useCountdown(sessionDate) {
  const [state, setState] = useState({ canJoin: false, minutesLeft: null, label: '' });

  useEffect(() => {
    if (!sessionDate) {
      setState({ canJoin: true, minutesLeft: 0, label: '' });
      return;
    }

    const tick = () => {
      const now        = new Date();
      const diffMs     = sessionDate - now;
      const diffMin    = Math.floor(diffMs / 60000);
      const diffSec    = Math.floor((diffMs % 60000) / 1000);

      if (diffMs <= 0) {
        // Session has started
        const minsAgo = Math.abs(diffMin);
        setState({
          canJoin: true,
          minutesLeft: 0,
          label: minsAgo < 120
            ? `Started ${minsAgo} min ago`
            : 'Session in progress',
        });
      } else if (diffMin <= EARLY_JOIN_MINUTES) {
        // Within early join window
        setState({
          canJoin: true,
          minutesLeft: diffMin,
          label: diffMin === 0
            ? `Starting in ${diffSec}s`
            : `Starts in ${diffMin}m ${diffSec}s`,
        });
      } else {
        // Too early
        const hrs  = Math.floor(diffMin / 60);
        const mins = diffMin % 60;
        const label = hrs > 0
          ? `Starts in ${hrs}h ${mins}m`
          : `Starts in ${diffMin}m`;
        setState({ canJoin: false, minutesLeft: diffMin, label });
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionDate]);

  return state;
}

// ── Main Component ─────────────────────────────────────────────
export default function MeetingJoinButton({
  platform = 'google_meet',
  url,
  date,
  time,
  size = 'md',        // 'sm' | 'md' | 'lg'
  showPlatformBadge = true,
  showCopyLink = true,
  allowEarlyJoin = true,   // if false, enforce countdown
  className = '',
}) {
  const [copied, setCopied]     = useState(false);
  const [triedApp, setTriedApp] = useState(false);

  const plat        = getPlatform(platform);
  const sessionDate = parseSessionTime(date, time);
  const { canJoin, minutesLeft, label } = useCountdown(
    allowEarlyJoin ? null : sessionDate
  );
  const joinUrls    = url ? getMeetingJoinUrl(platform, url) : null;
  const isDisabled  = !url || (!allowEarlyJoin && !canJoin);

  // ── Open meeting (try app first for Zoom/Teams) ─────────────
  const handleJoin = useCallback(() => {
    if (!joinUrls) return;

    if (joinUrls.appUrl && !triedApp) {
      // Try to open native app via deep link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = joinUrls.appUrl;
      document.body.appendChild(iframe);
      setTriedApp(true);

      // After 2s, if user is still here, open web URL as fallback
      setTimeout(() => {
        document.body.removeChild(iframe);
        // Open web URL in new tab as fallback
        window.open(joinUrls.webUrl, '_blank', 'noopener,noreferrer');
      }, 2000);
    } else {
      window.open(joinUrls.webUrl, '_blank', 'noopener,noreferrer');
    }
  }, [joinUrls, triedApp]);

  // ── Copy link to clipboard ──────────────────────────────────
  const handleCopy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  // ── No URL state ────────────────────────────────────────────
  if (!url) {
    return (
      <div className={`mjb-wrap mjb-${size} ${className}`}>
        <div className="mjb-pending">
          <span className="mjb-pending-icon">⏳</span>
          <div>
            <div className="mjb-pending-title">Meeting link pending</div>
            <div className="mjb-pending-sub">
              Your mentor will share the link before the session
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mjb-wrap mjb-${size} ${className}`}>

      {/* Platform badge */}
      {showPlatformBadge && (
        <div className="mjb-platform-badge" style={{ color: plat.color, background: plat.bgColor }}>
          <PlatformIcon id={plat.id} size={14} color={plat.color} />
          <span>{plat.label}</span>
        </div>
      )}

      {/* Countdown label */}
      {label && (
        <div className={`mjb-countdown ${canJoin ? 'active' : 'waiting'}`}>
          {canJoin ? '🟢' : '🕐'} {label}
        </div>
      )}

      {/* Main join button */}
      <button
        className={`mjb-btn mjb-btn-${size} ${isDisabled ? 'disabled' : 'active'}`}
        style={!isDisabled ? {
          background: plat.color,
          boxShadow: `0 4px 16px ${plat.color}40`,
        } : {}}
        onClick={handleJoin}
        disabled={isDisabled}
        title={isDisabled && minutesLeft
          ? `Meeting starts in ${minutesLeft} minutes`
          : `${plat.joinLabel}`
        }
      >
        <span className="mjb-btn-icon"><PlatformIcon id={plat.id} size={18} color="#fff" /></span>
        <span className="mjb-btn-text">
          {isDisabled && minutesLeft
            ? `Opens in ${minutesLeft > 60 ? `${Math.floor(minutesLeft/60)}h` : `${minutesLeft}m`}`
            : plat.joinLabel
          }
        </span>
        <span className="mjb-btn-arrow">↗</span>
      </button>

      {/* Copy link button */}
      {showCopyLink && (
        <button
          className={`mjb-copy ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
          title="Copy meeting link"
        >
          {copied ? '✅ Copied!' : '📋 Copy link'}
        </button>
      )}

      {/* Platform-specific tips */}
      {platform === 'zoom' && (
        <div className="mjb-tip">
          💡 Opens Zoom app if installed — or joins via browser
        </div>
      )}
      {platform === 'teams' && (
        <div className="mjb-tip">
          💡 Opens Teams app if installed — or joins via browser
        </div>
      )}
    </div>
  );
}

// ── Compact inline variant (for session cards) ─────────────────
export function MeetingJoinInline({ platform = 'google_meet', url, date, time }) {
  const plat = getPlatform(platform);
  const joinUrls = url ? getMeetingJoinUrl(platform, url) : null;

  if (!url) {
    return <span className="mjb-inline-pending">⏳ Link pending</span>;
  }

  return (
    <a
      href={joinUrls?.webUrl || url}
      target="_blank"
      rel="noopener noreferrer"
      className="mjb-inline-btn"
      style={{ background: plat.color }}
      onClick={(e) => {
        if (joinUrls?.appUrl) {
          e.preventDefault();
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = joinUrls.appUrl;
          document.body.appendChild(iframe);
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.open(joinUrls.webUrl, '_blank', 'noopener,noreferrer');
          }, 2000);
        }
      }}
    >
      <PlatformIcon id={plat.id} size={16} color="#fff" /> Join {plat.shortLabel} ↗
    </a>
  );
}

// ── Platform selector (for BookingPage / EventForm) ────────────
export function MeetingPlatformSelector({ value, onChange, showHints = true }) {
  const { MEETING_PLATFORMS } = require('../config/meetingConfig');

  return (
    <div className="mps-wrap">
      {MEETING_PLATFORMS.map(p => (
        <button
          key={p.id}
          type="button"
          className={`mps-option ${value === p.id ? 'selected' : ''}`}
          style={value === p.id ? {
            borderColor: p.color,
            background: p.bgColor,
          } : {}}
          onClick={() => onChange(p.id)}
        >
          <span className="mps-icon"><PlatformIcon id={p.id} size={22} bg={value === p.id ? p.color : '#9ca3af'} /></span>
          <div className="mps-text">
            <div className="mps-label" style={value === p.id ? { color: p.color } : {}}>
              {p.label}
            </div>
            {showHints && (
              <div className="mps-hint">{p.hint}</div>
            )}
          </div>
          {value === p.id && (
            <div className="mps-check" style={{ color: p.color }}>✓</div>
          )}
        </button>
      ))}
    </div>
  );
}
